import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/primitives/Input';
import { Spinner } from '@/components/primitives/Spinner';
import { Icon } from '@/components/Icon';
import { apiErrorText } from '@/api/opengater/errors';
import { dragHasFiles, filesOf, imageFileProblem } from './imageFiles';
import f from './NewsImageField.module.css';
/**
 * An article picture on its settings row: the picture cropped the way the site
 * crops it, its address, and uploading — pick a file, drop it or paste it.
 */
export function NewsImageField({ label, hint, value, onChange, onUpload, disabled = false, shape, }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    // The picked file itself while it uploads, so the frame doesn't wait for the server.
    const [local, setLocal] = useState(null);
    const [error, setError] = useState(null);
    const [brokenSrc, setBrokenSrc] = useState(null);
    const [dragging, setDragging] = useState(false);
    const src = local ?? value.trim();
    const broken = !local && !!src && brokenSrc === src;
    const locked = disabled || uploading;
    useEffect(() => () => void (local && URL.revokeObjectURL(local)), [local]);
    const upload = async (files) => {
        const file = files[0];
        if (!file || locked)
            return;
        const problem = imageFileProblem(file);
        if (problem) {
            setError(problem);
            return;
        }
        setError(null);
        setUploading(true);
        setLocal(typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null);
        try {
            onChange(await onUpload(file));
        }
        catch (e) {
            const reason = apiErrorText(e, e instanceof Error ? e.message : 'ошибка');
            setError(`Не удалось загрузить ${file.name}: ${reason}`);
        }
        finally {
            setUploading(false);
            setLocal(null);
        }
    };
    const pick = () => fileRef.current?.click();
    return (_jsxs("div", { className: f.field, "data-shape": shape, "data-dragging": dragging || undefined, onDragOver: (event) => {
            if (locked || !dragHasFiles(event.dataTransfer?.types))
                return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            setDragging(true);
        }, onDragLeave: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                setDragging(false);
        }, onDrop: (event) => {
            setDragging(false);
            if (locked || !dragHasFiles(event.dataTransfer?.types))
                return;
            event.preventDefault();
            void upload(filesOf(event.dataTransfer.files));
        }, onPaste: (event) => {
            const files = filesOf(event.clipboardData?.files);
            if (files.length === 0 || event.clipboardData.getData('text/plain'))
                return;
            event.preventDefault();
            void upload(files);
        }, children: [_jsxs("div", { className: f.frame, "data-empty": !src || undefined, children: [src && !broken && (_jsx("img", { src: src, alt: "", "data-local": local ? true : undefined, onError: () => setBrokenSrc(src) }, src)), broken && (_jsxs("span", { className: f.broken, children: [_jsx(Icon, { name: "warn", size: 16 }), "\u041D\u0435 \u043E\u0442\u043A\u0440\u044B\u0432\u0430\u0435\u0442\u0441\u044F"] })), !src && (_jsxs("button", { type: "button", className: f.pick, "aria-label": `Загрузить: ${label}`, disabled: locked, onClick: pick, children: [_jsx(Icon, { name: "upload", size: 16 }), _jsx("span", { children: "\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C" })] })), uploading && (_jsxs("span", { className: f.busy, children: [_jsx(Spinner, { size: 14 }), "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026"] }))] }), _jsxs("div", { className: f.head, children: [_jsxs("div", { className: f.label, children: [_jsx("span", { className: f.labelText, children: label }), hint && _jsx("span", { className: f.hint, children: hint })] }), value.trim() !== '' && (_jsxs("div", { className: f.actions, children: [_jsx("button", { type: "button", className: f.action, disabled: locked, onClick: pick, children: "\u0417\u0430\u043C\u0435\u043D\u0438\u0442\u044C" }), _jsx("button", { type: "button", className: f.action, disabled: locked, onClick: () => {
                                    setError(null);
                                    onChange('');
                                }, children: "\u0423\u0431\u0440\u0430\u0442\u044C" })] }))] }), _jsxs("div", { className: f.address, children: [_jsx(Input, { className: f.url, value: value, type: "url", mono: true, disabled: disabled, placeholder: "https://\u2026", "aria-label": label, onChange: (event) => onChange(event.target.value) }), error && (_jsx("span", { className: f.error, role: "alert", children: error }))] }), _jsx("input", { ref: fileRef, type: "file", accept: "image/*", hidden: true, disabled: locked, onChange: (event) => {
                    void upload(filesOf(event.target.files));
                    event.target.value = '';
                } })] }));
}
